import React, { useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { FaCalendarAlt, FaUser, FaArrowLeft } from 'react-icons/fa';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to navigate back with clicked post ID
  const handleBackClick = (e) => {
    e.preventDefault();
    
    // Always use the current post ID as that's what we're viewing
    const postId = parseInt(id, 10);
    
    // Store in session storage for backup
    sessionStorage.setItem('blogClickedPostId', postId);
    
    // Navigate back to blog page with clicked post ID
    // Use replace and state.fromBlogPost flag for faster detection
    navigate('/blog', { 
      state: { 
        clickedPostId: postId,
        fromBlogPost: true
      },
      replace: true // Use replace to ensure clean history
    });
  };
  
  // Sample blog posts data (in a real app, this would be fetched from an API)
  const blogPosts = useMemo(() => [
    {
      id: 1,
      title: "5 Key Benefits of Professional Demolition Services",
      date: "November 15, 2023",
      author: "Michael Johnson",
      category: "Demolition",
      content: `
        <p>Professional demolition offers numerous advantages over DIY approaches. Learn how expert demolition services can save time, ensure safety, and provide cost-effective solutions for your project.</p>
        
        <h2>1. Enhanced Safety Measures</h2>
        <p>Professional demolition companies implement comprehensive safety protocols to protect workers, property owners, and surrounding structures. They use specialized equipment and techniques to control debris, dust, and potential hazards during the demolition process.</p>
        
        <h2>2. Time and Cost Efficiency</h2>
        <p>Though DIY demolition might seem cost-effective initially, professional services often save money in the long run. Experts complete the job faster with fewer errors, reducing labor costs and preventing costly mistakes that could damage surrounding structures.</p>
        
        <h2>3. Proper Waste Management</h2>
        <p>Professional demolition services include responsible waste disposal and recycling. They separate materials that can be recycled or reused, minimizing landfill impact and potentially reducing disposal costs.</p>
        
        <h2>4. Access to Specialized Equipment</h2>
        <p>Demolition professionals have access to specialized tools and heavy machinery that aren't typically available to homeowners. This equipment allows them to handle demolition projects of any size and complexity efficiently.</p>
        
        <h2>5. Regulatory Compliance</h2>
        <p>Professional demolition contractors understand local building codes, permit requirements, and environmental regulations. They ensure your project complies with all legal requirements, preventing potential fines or legal issues.</p>
      `,
      image: "/images/blog/5_Key_Benefits_of_Professional_Demolition_Service_f21b1622-b0ac-41fe-97b1-d48fcab095a5_3.jpg",
    },
    {
      id: 2,
      title: "Preparing Your Property for Site Clearing: A Complete Guide",
      date: "October 22, 2023",
      author: "Sarah Thompson",
      category: "Site Clearing",
      content: `
        <p>Proper preparation before site clearing can save you time and money. This guide covers everything you need to do before the site clearing team arrives, from permits to utility marking.</p>
        
        <h2>Obtain Necessary Permits</h2>
        <p>Before any site clearing begins, ensure you have all required permits from local authorities. This may include land disturbance permits, environmental clearances, and building permits if construction will follow clearing.</p>
        
        <h2>Mark Boundaries and Protect What Stays</h2>
        <p>Clearly mark the boundaries of your property and the specific areas to be cleared. Identify trees, structures, or natural features that should be preserved and protect them with barriers or marking tape.</p>
        
        <h2>Locate and Mark Utilities</h2>
        <p>Contact your local utility locating service to mark underground utilities like gas, water, sewer, and electrical lines. This critical step prevents dangerous and costly damage during clearing operations.</p>
        
        <h2>Plan for Erosion Control</h2>
        <p>Develop a strategy for managing erosion, especially if your property is on a slope. This may include silt fences, erosion control blankets, or temporary vegetation to prevent soil runoff during and after clearing.</p>
        
        <h2>Arrange for Material Disposal or Recycling</h2>
        <p>Determine how cleared materials will be handled. Options include on-site burning (where permitted), chipping for mulch, hauling to a disposal facility, or selling valuable timber. Having this plan in place before clearing begins makes the process more efficient.</p>
      `,
      image: "/images/services/realistic_scene_showing_a_skid_steer_loader.jpg",
    },
    {
      id: 3,
      title: "Environmentally Friendly Demolition Practices",
      date: "September 8, 2023",
      author: "Robert Miller",
      category: "Demolition",
      content: `
        <p>Modern demolition isn't just about tearing things down. Learn how eco-friendly demolition practices are reducing landfill waste, recycling materials, and minimizing environmental impact.</p>
        
        <h2>Deconstruction vs. Traditional Demolition</h2>
        <p>Eco-friendly demolition often begins with deconstruction—the systematic disassembly of a structure to salvage usable materials. Unlike traditional demolition that focuses on quick removal, deconstruction preserves valuable resources that would otherwise end up in landfills.</p>
        
        <h2>Material Recycling and Reuse</h2>
        <p>Modern demolition companies sort materials for recycling and reuse. Concrete can be crushed for aggregate, metals can be melted down, and wood can be repurposed or used as biomass fuel. Even materials like glass, drywall, and asphalt shingles have recycling potential.</p>
        
        <h2>Dust and Pollution Control</h2>
        <p>Environmentally conscious demolition contractors use water sprays, misting systems, and containment barriers to control dust. This protects air quality and prevents hazardous particles from spreading to surrounding areas.</p>
        
        <h2>Hazardous Material Management</h2>
        <p>Green demolition includes proper identification and safe removal of hazardous materials like asbestos, lead paint, and PCBs. Specialized contractors ensure these materials are handled according to strict environmental regulations.</p>
        
        <h2>Energy-Efficient Equipment</h2>
        <p>Many demolition companies now use energy-efficient or hybrid machinery to reduce fuel consumption and emissions. Some even power their equipment with biodiesel or other alternative fuels to further minimize their carbon footprint.</p>
      `,
      image: "/images/blog/a_picture_of_the_world_with_a_smile_cartoonish_--_f135712f-de6a-45bc-86cb-7c36bbea67b5_1.jpg",
    },
    {
      id: 4,
      title: "The Complete Guide to Concrete Removal",
      date: "August 17, 2023",
      author: "Jennifer Davis",
      category: "Concrete Removal",
      content: `
        <p>Removing concrete requires specialized equipment and expertise. This guide explains the different methods of concrete removal, from jackhammering to concrete sawing and beyond.</p>
      `,
      image: "/images/blog/a_picture_of_some_concrete_--v_6.1_a2ed6a0a-4083-440c-9f45-737a9544787c_3.jpeg",
    },
    {
      id: 5,
      title: "How to Choose the Right Excavation Company",
      date: "July 25, 2023",
      author: "David Wilson",
      category: "Excavation",
      content: `
        <p>Selecting the right excavation contractor is crucial for project success. Discover the key factors to consider, questions to ask, and red flags to watch for when hiring an excavation company.</p>
      `,
      image: "/images/blog/how_to_choose_the_right_excavation_company_--v_6._da974dfb-b524-4c90-9e23-2ba925737c0e_2.jpg",
    }
  ], []);
  
  // Find the post with the matching ID
  const post = blogPosts.find(post => post.id === parseInt(id));
  
  if (!post) {
    return (
      <Container>
        <div className="not-found">
          <h2>Post Not Found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <BackButton to="/blog">Back to Blog</BackButton>
        </div>
      </Container>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{post.title} | Florida's Top Site Service & Demolition</title>
        <meta name="description" content={post.content.substring(0, 160).replace(/<[^>]+>/g, '')} />
      </Helmet>
      
      <HeroSection style={{backgroundImage: `url(${post.image})`}}>
        <div className="overlay">
          <div className="container">
            <CategoryTag>{post.category}</CategoryTag>
            <h1>{post.title}</h1>
            <PostMeta>
              <MetaItem>
                <FaCalendarAlt />
                <span>{post.date}</span>
              </MetaItem>
              <MetaItem>
                <FaUser />
                <span>{post.author}</span>
              </MetaItem>
            </PostMeta>
          </div>
        </div>
      </HeroSection>
      
      <Container>
        <Content>
          <PostContent dangerouslySetInnerHTML={{ __html: post.content }} />
          
          <BackButton to="/blog" onClick={handleBackClick}>
            <FaArrowLeft /> Back to All Posts
          </BackButton>
        </Content>
      </Container>
    </>
  );
};

const HeroSection = styled.section`
  height: 50vh;
  min-height: 400px;
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.8) 100%);
    display: flex;
    align-items: center;
  }
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    width: 100%;
  }
  
  h1 {
    color: white;
    font-size: 3.5rem;
    margin: 1.5rem 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  background-color: #1a1a1a;
  min-height: 100vh;
  
  .not-found {
    text-align: center;
    padding: 5rem 0;
    
    h2 {
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
      color: white;
    }
    
    p {
      font-size: 1.2rem;
      margin-bottom: 2rem;
      color: #bbb;
    }
  }
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CategoryTag = styled.span`
  display: inline-block;
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const PostMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  
  svg {
    margin-right: 0.5rem;
    color: var(--primary-color);
  }
`;

const PostContent = styled.div`
  margin: 3rem 0;
  font-size: 1.1rem;
  line-height: 1.8;
  color: white;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 2.5rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: var(--primary-color);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding-bottom: 0.8rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 2rem 0;
  }
  
  blockquote {
    border-left: 4px solid var(--primary-color);
    padding-left: 1.5rem;
    margin: 2rem 0;
    font-style: italic;
    color: #ccc;
    background-color: rgba(0, 0, 0, 0.3);
    padding: 1rem 1.5rem;
    border-radius: 0 8px 8px 0;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: rgba(0, 0, 0, 0.4);
  border: 1px solid var(--primary-color);
  color: white;
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-decoration: none;
  margin: 1rem 0;
  
  svg {
    margin-right: 0.5rem;
    color: var(--primary-color);
  }
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-3px);
    
    svg {
      color: white;
    }
  }
`;

export default BlogPost; 